// Hub menu + cutoff times, cached OUTSIDE the React tree.
//
// /menu and /checkout are sibling routes, so navigating to checkout unmounts Home and
// every piece of its state dies with it — including the menu it just fetched. Coming
// back re-requested everything and blocked the whole page on the round-trip, which is
// the multi-second stall users see on "back".
//
// Module scope survives that unmount. Home hydrates from here synchronously on mount,
// paints immediately, and revalidates in the background.

const DEFAULT_HUB_ID = "6a4b63c1f3fdb51880c636b4";

const cache = new Map(); // hubId -> { menu, cutoff, fetchedAt }

export const getCachedHubData = (hubId) =>
  hubId ? cache.get(hubId) || null : null;

export const setCachedHubData = (hubId, { menu, cutoff }) => {
  if (!hubId) return;
  cache.set(hubId, { menu, cutoff, fetchedAt: Date.now() });
};

export const clearCachedHubData = (hubId) => {
  if (hubId) cache.delete(hubId);
  else cache.clear();
};

/**
 * The address Home should start with, read straight from localStorage.
 *
 * Home used to read this in an effect, so its first render had no hub — which sent the
 * fetch effect down its "no hub" branch and painted the "Add Location" header with
 * "Serving Soon" cards and the "No menu available right now" hero, i.e. a confident
 * "there is no food" while the app simply had not looked yet. localStorage is
 * synchronous; there is no reason to take a render to read it.
 */
export const readStoredAddress = () => {
  try {
    const primaryAddress = localStorage.getItem("primaryAddress");
    if (primaryAddress && primaryAddress !== "null") {
      return { address: JSON.parse(primaryAddress), fromStorage: true };
    }

    const currentLocation = localStorage.getItem("currentLocation");
    if (currentLocation && currentLocation !== "null") {
      return { address: JSON.parse(currentLocation), fromStorage: true };
    }

    const defaultHubData = localStorage.getItem("defaultHubData");
    if (defaultHubData && defaultHubData !== "null") {
      const parsed = JSON.parse(defaultHubData);
      return {
        address: {
          hubId: parsed.hubId || parsed._id || DEFAULT_HUB_ID,
          hubName: parsed.hubName || parsed.name || "Default Hub",
          fullAddress: parsed.address || "Select your location to view menu",
          isDefaultHub: true,
          location: parsed.location || { type: "Point", coordinates: [0, 0] },
        },
        fromStorage: true,
        // The caller persists this back so the rest of the app sees the same shape.
        persistAsCurrentLocation: true,
      };
    }
  } catch (error) {
    console.error("Error reading stored address:", error);
  }
  return { address: null, fromStorage: false };
};

// Same rule Home applies: the address's hub, or the default hub for guests.
export const resolveHubId = (address, user) => {
  if (address?.hubId) return address.hubId;
  if (!user) return DEFAULT_HUB_ID;
  return null;
};
